import { Icon, cx, Badge, DataStateBadge, RuleResult, Button, ConfirmEnterButton, IconButton, Avatar, Tooltip } from './core';
import { etAndLocal } from './time';
import React, { useState, useId } from 'react';

/* ---------- Decision language ---------- */
/** Maps backend labels ("ENTER_PRE_OPEN", "no trade", "NO_TRADE") onto the six-value vocabulary. */
export function normDecision(v) {
  if (!v) return 'NO TRADE';
  const s = String(v).toUpperCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (s === 'NOTRADE') return 'NO TRADE';
  return s;
}
export const DECISIONS = {
  'PROVISIONAL': { key: 'provisional', icon: 'Pencil', rank: 0, desc: 'Pre-market build. Not actionable; will be re-run.' },
  'ENTER PRE-OPEN': { key: 'enter-pre-open', icon: 'Timer', rank: 4, desc: 'Qualifies before the open. Rare and conditional on the official open confirming.' },
  'WAIT FOR OPEN': { key: 'wait-for-open', icon: 'Hourglass', rank: 2, desc: 'Nothing to act on until the 09:33 official-open reset.' },
  'ENTER': { key: 'enter', icon: 'CircleCheck', rank: 5, desc: 'The primary candidate passed every rule family. Awaiting human acknowledgement.' },
  'WAIT': { key: 'wait', icon: 'Clock', rank: 3, desc: 'Close to qualifying; re-evaluated at the next checkpoint.' },
  'NO TRADE': { key: 'no-trade', icon: 'Ban', rank: 1, desc: 'The gate is closed. The expected, dignified outcome.' },
};
export function DecisionBadge({ decision = 'NO TRADE', size = 'standard', muted = false, ...rest }) {
  decision = normDecision(decision); const d = DECISIONS[decision] || DECISIONS['NO TRADE'];
  return (
    <span {...rest} className={cx('sn-dec', 'sn-dec-' + size, 'sn-dec-' + d.key)} data-muted={muted || undefined} title={d.desc}>
      <Icon name={d.icon} /><span className="sn-vh">Decision: </span>{decision}
    </span>
  );
}
export function PathChip({ path = 'UNCLASSIFIED' }) {
  const un = path === 'UNCLASSIFIED';
  return <span className={cx('sn-path', un && 'sn-path-unclassified')} aria-label={`Path ${path}`}><Icon name={un ? 'CircleDashed' : 'Waypoints'} />PATH · <b>{path}</b></span>;
}
/** The one loud thing on a screen. */
export function DecisionHero({ decision = 'NO TRADE', run, time, runId, path, reason, figure, figureLabel, compact, children, frozen = true, ...rest }) {
  decision = normDecision(decision); const d = DECISIONS[decision] || DECISIONS['NO TRADE']; const hid = useId();
  return (
    <section {...rest} className={cx('sn-hero', compact && 'sn-hero-compact')} data-decision={d.key} aria-labelledby={hid}>
      <div>
        <div className="sn-hero-kicker">
          <span className="sn-micro">Decision{run ? ` · ${run}` : ''}</span>
          {time && <span className="sn-num">{time}</span>}
          {frozen && <DataStateBadge state="FROZEN" />}
          {path && <PathChip path={path} />}
          {runId && <span className="sn-num" style={{ fontSize: 12 }}>{runId}</span>}
        </div>
        <h2 className="sn-hero-label" id={hid}><span className="sn-hero-glyph"><Icon name={d.icon} /></span>{decision}</h2>
        <p className="sn-hero-sub">{reason || d.desc}</p>
        {children}
      </div>
      {figure && (
        <div className="sn-hero-side">
          <div className="sn-stat sn-hero-stat">
            <span className="sn-stat-k">{figureLabel}</span>
            <span className="sn-hero-figure">{figure}</span>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------- Numbers ---------- */
const PREC = { index: 1, price: 2, prob: 2, pct: 1, usd: 0, pts: 1, strike: 0, ratio: 2 };
export function fmt(v, kind = 'price', { signed = false } = {}) {
  if (v == null || Number.isNaN(v)) return '—';
  const p = PREC[kind] ?? 2;
  const s = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: p, maximumFractionDigits: p });
  const sign = v < 0 ? '−' : signed && v > 0 ? '+' : '';
  return sign + (kind === 'usd' ? '$' : '') + s + (kind === 'pct' ? '%' : '');
}
const STATE_GLYPH = { stub: 'FlaskRound', stale: 'Hourglass', delayed: 'Clock', unavailable: 'CircleSlash', frozen: 'Lock' };
/** A number that carries its provenance in its own appearance. */
export function Num({ value, kind = 'price', signed, state = 'live', unit, source, ts, className, style }) {
  const txt = state === 'unavailable' ? '—' : typeof value === 'string' ? value : fmt(value, kind, { signed });
  const lbl = `${txt}${unit ? ' ' + unit : ''}${state !== 'live' ? ', ' + state.toUpperCase() : ''}${source ? ', source ' + source : ''}${ts ? ' at ' + ts : ''}`;
  const node = (
    <span className={cx('sn-n', className)} data-state={state} aria-label={lbl} style={style}>
      <span className="sn-n-v">{txt}</span>{unit && <span className="sn-n-unit">{unit}</span>}
      {STATE_GLYPH[state] && <span className="sn-n-glyph"><Icon name={STATE_GLYPH[state]} /></span>}
    </span>
  );
  if (!source) return node;
  return <Tooltip content={<span>{source}<br /><span className="sn-num">{ts}</span> · {state.toUpperCase()}</span>}>{React.cloneElement(node, { tabIndex: 0 })}</Tooltip>;
}
export function Delta({ value, kind = 'index', pct, size }) {
  const dir = value > 0 ? 'up' : value < 0 ? 'down' : 'flat';
  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '◆';
  return (
    <span className={cx('sn-delta', 'sn-price-' + dir)} style={size ? { fontSize: size } : null} aria-label={`${dir === 'flat' ? 'unchanged' : dir} ${fmt(Math.abs(value), kind)}${pct != null ? ' (' + fmt(Math.abs(pct), 'pct') + ')' : ''}`}>
      <span className="sn-arrow" aria-hidden="true">{arrow}</span>{fmt(value, kind, { signed: true })}{pct != null && <span style={{ opacity: .9 }}>({fmt(pct, 'pct', { signed: true })})</span>}
    </span>
  );
}
export function StatCell({ label, value, kind, signed, unit, state, sub, size = 'lg', delta, info, source, ts, ...rest }) {
  return (
    <div {...rest} className={cx('sn-stat', size !== 'lg' && 'sn-stat-' + size)}>
      <span className="sn-stat-k">{label}{info && <Icon name="Info" size={12} label={info} />}</span>
      <span className="sn-stat-v"><Num value={value} kind={kind} signed={signed} unit={unit} state={state} source={source} ts={ts} /></span>
      {(sub || delta != null) && <span className="sn-stat-sub">{delta != null && <Delta value={delta} />}{sub}</span>}
    </div>
  );
}
function QBar({ q10, q50, q90, act, lo, hi }) {
  const x = v => ((v - lo) / (hi - lo)) * 100 + '%';
  return (
    <div className="sn-qbar" aria-hidden="true">
      <span className="sn-qbar-track" />
      <span className="sn-qbar-band" style={{ left: x(q10), width: `calc(${x(q90)} - ${x(q10)})` }} />
      <span className="sn-qbar-mid" style={{ left: x(q50) }} />
      {act != null && <span className="sn-qbar-act" style={{ left: x(act) }} />}
    </div>
  );
}
/** `lo`/`hi` bound the quantile bars; without them the range is taken from the forecast itself. */
export function ForecastStrip({ f, state = 'live', lo: loProp, hi: hiProp, priorClose }) {
  if (!f || !f.open || !f.high || !f.low || !f.close) return null;
  const vals = [f.high.q10, f.high.q90, f.low.q10, f.low.q90, f.close.q10, f.close.q90, f.open.f, f.open.a].filter(v => v != null && !Number.isNaN(v));
  const span = vals.length ? Math.max(...vals) - Math.min(...vals) : 0;
  const lo = loProp ?? (vals.length ? Math.min(...vals) - span * 0.1 : 0);
  const hi = hiProp ?? (vals.length ? Math.max(...vals) + span * 0.1 : 1);
  return (
    <div className="sn-forecast" role="group" aria-label="Session forecast">
      <div className="sn-stat"><span className="sn-stat-k">Open · forecast</span><span className="sn-stat-v"><Num value={f.open.f} kind="index" state={state} /></span>
        <span className="sn-stat-sub">Actual <Num value={f.open.a} kind="index" /> · err <Num value={f.open.a != null && f.open.f != null ? f.open.a - f.open.f : null} kind="index" signed /></span></div>
      <div className="sn-stat"><span className="sn-stat-k">High q10 / q50 / q90</span><span className="sn-stat-v"><Num value={f.high.q50} kind="index" state={state} /></span>
        <span className="sn-stat-sub sn-num">{fmt(f.high.q10, 'index')} – {fmt(f.high.q90, 'index')}</span><QBar {...f.high} lo={lo} hi={hi} /></div>
      <div className="sn-stat"><span className="sn-stat-k">Low q10 / q50 / q90</span><span className="sn-stat-v"><Num value={f.low.q50} kind="index" state={state} /></span>
        <span className="sn-stat-sub sn-num">{fmt(f.low.q10, 'index')} – {fmt(f.low.q90, 'index')}</span><QBar {...f.low} lo={lo} hi={hi} /></div>
      <div className="sn-stat"><span className="sn-stat-k">Close q50</span><span className="sn-stat-v"><Num value={f.close.q50} kind="index" state={state} /></span>
        <span className="sn-stat-sub sn-num">q10 {fmt(f.close.q10, 'index')} · q90 {fmt(f.close.q90, 'index')}</span><QBar {...f.close} lo={lo} hi={hi} /></div>
      <div className="sn-stat"><span className="sn-stat-k">P(up) / P(down)</span><span className="sn-stat-v"><Num value={f.pUp} kind="prob" state={state} /><span className="sn-faint" style={{ fontSize: 18 }}> / </span><Num value={f.pDown} kind="prob" state={state} style={{ fontSize: 18, color: 'var(--text-secondary)' }} /></span>
        <span className="sn-stat-sub">Prior close {fmt(priorClose, 'index')}</span></div>
    </div>
  );
}
/** A calibrated read: point estimate + interval on a fixed 0–1 axis, with the threshold marked. Not a progress bar. */
/** Renders nothing without a calibrated `value` and its `ci`; the threshold marker is drawn only when a `threshold` is given. */
export function ConfidenceMeter({ label = 'P(profit), calibrated', value, ci, threshold, calibration, min = 0.5, max = 1 }) {
  if (value == null || !ci) return null;
  const hasThr = threshold != null;
  const x = v => ((v - min) / (max - min)) * 100 + '%';
  const ticks = [0.5, 0.6, 0.7, 0.8, 0.9, 1.0].filter(t => t >= min && t <= max);
  return (
    <div className="sn-conf" role="img" aria-label={`${label} ${value.toFixed(2)}, 80% interval ${ci[0].toFixed(2)} to ${ci[1].toFixed(2)}${hasThr ? `, threshold ${threshold.toFixed(2)}` : ''}`}>
      <div className="sn-conf-top"><span className="sn-stat-k" style={{ flex: 1 }}>{label}</span><span className="sn-conf-v">{value.toFixed(2)}</span></div>
      <div className="sn-conf-scale" aria-hidden="true">
        <span className="sn-conf-axis" />
        {ticks.map(t => <React.Fragment key={t}><span className="sn-conf-tick" style={{ left: x(t) }} /><span className="sn-conf-tlabel" style={{ left: x(t) }}>{t.toFixed(1)}</span></React.Fragment>)}
        {hasThr && <span className="sn-conf-thr" style={{ left: x(threshold) }} />}
        <span className="sn-conf-ci" style={{ left: x(ci[0]), width: `calc(${x(ci[1])} - ${x(ci[0])})` }} />
        <span className="sn-conf-pt" style={{ left: x(value) }} />
      </div>
      <span className="sn-conf-note">80% interval {ci[0].toFixed(2)}–{ci[1].toFixed(2)}{hasThr ? ` · threshold ${threshold.toFixed(2)} (dashed)` : ''}{calibration ? ` · ${calibration}` : ''}</span>
    </div>
  );
}

/* ---------- Checkpoints ---------- */
export function CheckpointTimeline({ checkpoints, orientation = 'horizontal', showDecisions = true, compact, loudCurrent = true }) {
  const iconFor = s => ({ done: 'Check', frozen: 'Lock', current: 'CircleDot', pending: 'Clock', failed: 'X' })[s];
  return (
    <div className={orientation === 'vertical' ? 'sn-tl-vert' : undefined}>
      <ol className="sn-tl" aria-label="Checkpoints R1 to R6" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {checkpoints.map(c => (
          <li key={c.id} className="sn-tl-node" data-s={c.s} aria-current={c.s === 'current' ? 'step' : undefined}>
            <div className="sn-tl-top"><span className="sn-tl-dot" aria-hidden="true"><Icon name={iconFor(c.s)} /></span><span className="sn-tl-line" /></div>
            <span className="sn-tl-id">{c.id}<span className="sn-tl-time">{etAndLocal(c.time, { day: c.day })}</span></span>
            {!compact && <span className="sn-tl-name">{c.name}</span>}
            {showDecisions && (c.decision ? <DecisionBadge decision={c.decision} size="compact" muted={!loudCurrent || c.s !== 'current'} /> : <span className="sn-small sn-faint">{c.s === 'pending' ? 'Scheduled' : '—'}</span>)}
            <span className="sn-vh">{c.s === 'frozen' ? 'Frozen, sealed' : c.s}</span>
            {c.recalc && (
              <span className="sn-tl-sib" title={c.recalc.reason}><Icon name="RotateCw" /><span className="sn-num">{c.recalc.id}</span><span className="sn-num sn-faint">{c.recalc.time}</span></span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
/** Renders nothing until it has a `target` and a `remaining` time; `progress` (0–1) is optional. */
export function Countdown({ target, at, remaining, progress, variant = 'block', state }) {
  if (!target || !remaining) return null;
  if (variant === 'inline') return <span className="sn-cd-inline"><span className="sn-faint">Next {target} in</span><span className="sn-cd-v" role="timer" aria-live="off">{remaining}</span></span>;
  return (
    <div className="sn-cd">
      <span className="sn-stat-k">Next run · {target}{at ? ` ${at}` : ''}</span>
      <span className="sn-cd-v" role="timer" aria-live="off" aria-label={`${target} in ${remaining}`}>{remaining}</span>
      {progress != null && <span className="sn-cd-bar" aria-hidden="true"><i style={{ width: Math.max(0, Math.min(1, progress)) * 100 + '%' }} /></span>}
      {state && <span className="sn-help">{state}</span>}
    </div>
  );
}

/* ---------- Ticket ---------- */
export function TradeTicket({ t, decision, acknowledged = false, acknowledgedAt, compact, muted = true, onConfirm, note, confirmDisabled, ...rest }) {
  const tid = useId();
  if (!t || !t.legs || t.legs.length < 2) return null;
  return (
    <section {...rest} className="sn-ticket" aria-labelledby={tid}>
      <div className="sn-ticket-h">
        <DecisionBadge decision={decision} size="compact" muted={muted} />
        <h3 id={tid}>{t.strategy} · {fmt(t.legs[0].strike, 'strike')} / {fmt(t.legs[1].strike, 'strike')}</h3>
        <PathChip path={t.path} />
        <span style={{ marginLeft: 'auto' }} className="sn-ro"><Icon name="Eye" size={12} />Read-only ticket</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="sn-ticket-legs">
          <thead><tr><th>Leg</th><th>Contract</th><th className="r">Strike</th><th className="r">Bid</th><th className="r">Ask</th><th className="r">Mid</th>{!compact && <th className="r">Δ</th>}</tr></thead>
          <tbody>{t.legs.map((l, i) => (
            <tr key={i}><td>{l.side} {l.qty} {l.type}</td><td className="sn-num" style={{ fontSize: 12, fontWeight: 500 }}>{l.occ}</td>
              <td className="r sn-num">{fmt(l.strike, 'strike')}</td><td className="r sn-num">{fmt(l.bid)}</td><td className="r sn-num">{fmt(l.ask)}</td><td className="r sn-num">{fmt(l.mid)}</td>{!compact && <td className="r sn-num">{fmt(l.delta, 'prob', { signed: true })}</td>}</tr>
          ))}</tbody>
        </table>
      </div>
      <div className="sn-ticket-grid" style={compact ? { gridTemplateColumns: 'repeat(2, 1fr)' } : null}>
        <StatCell size="md" label="Net credit" value={t.credit} kind="price" sub={`width ${t.width} · slippage ${fmt(t.slippage)}`} />
        <StatCell size="md" label="Max risk" value={t.maxRiskUsd} kind="usd" sub={`${fmt(t.maxRisk)} × 100`} />
        <StatCell size="md" label="Net EV / max risk" value={t.evPct} kind="pct" signed sub={`${fmt(t.evUsd, 'price', { signed: true })} per spread`} />
        <StatCell size="md" label="Breakeven" value={t.breakeven} kind="price" sub={`${fmt(t.distance, 'pts')} pts from spot`} />
      </div>
      <div className="sn-ticket-f">
        {t.pProfit != null && t.pProfitCI && <ConfidenceMeter value={t.pProfit} ci={t.pProfitCI} threshold={t.pProfitThreshold} calibration={t.calibration} />}
        {normDecision(decision) === 'ENTER' && onConfirm && <ConfirmEnterButton confirmed={acknowledged} confirmedAt={acknowledgedAt} candidateId={t.id} onConfirm={onConfirm} disabled={confirmDisabled} />}
        <span className="sn-small sn-faint">Candidate <span className="sn-num">{t.id}</span>{note ? ` · ${note}` : ''} · Sentinel places no orders.</span>
      </div>
    </section>
  );
}

/* ---------- Audit ---------- */
export function AuditMatrix({ families, candidateId, dense, stacked }) {
  const all = families.flatMap(f => f.rows); const count = r => all.filter(x => x.res === r).length;
  if (stacked) return (
    <div>
      <table className="sn-audit sn-audit-stacked" aria-label={`Threshold audit for ${candidateId || 'candidate'}`}>
        <thead><tr><th scope="col">Rule</th><th scope="col">Check · observed vs threshold</th><th scope="col">Result</th></tr></thead>
        <tbody>{families.map(f => (
          <React.Fragment key={f.fam}>
            <tr className="sn-fam"><td colSpan={3}>{f.fam} · {f.famName}</td></tr>
            {f.rows.map(r => (
              <tr key={r.id} data-result={r.res}><td className="rid">{r.id}</td>
                <td className="rtext">{r.text}<span className="sn-audit-vals"><b>{r.obs}</b> vs {r.thr}</span></td><td><RuleResult result={r.res} /></td></tr>
            ))}
          </React.Fragment>))}</tbody>
      </table>
      <div className="sn-audit-summary"><span>{count('PASS')} PASS</span><span>{count('FAIL')} FAIL</span><span>{count('NA')} NA</span><span>{count('UNCONFIRMED')} UNCONFIRMED</span></div>
    </div>
  );
  return (
    <div>
      <table className="sn-audit" aria-label={`Threshold audit for ${candidateId || 'candidate'}`}>
        <thead><tr><th scope="col">Rule</th><th scope="col">Checks</th><th scope="col" className="r">Observed</th><th scope="col" className="r">Threshold</th><th scope="col">Result</th></tr></thead>
        <tbody>
          {families.map(f => (
            <React.Fragment key={f.fam}>
              <tr className="sn-fam"><td colSpan={5}>{f.fam} · {f.famName}</td></tr>
              {f.rows.map(r => (
                <tr key={r.id} data-result={r.res}>
                  <td className="rid">{r.id}</td><td className="rtext">{r.text}</td>
                  <td className="r num">{r.obs}</td><td className="r num" style={{ color: 'var(--text-secondary)' }}>{r.thr}</td>
                  <td><RuleResult result={r.res} /></td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <div className="sn-audit-summary"><span>{count('PASS')} PASS</span><span>{count('FAIL')} FAIL</span><span>{count('NA')} NA</span><span>{count('UNCONFIRMED')} UNCONFIRMED</span><span>Evaluated L → E → T → C → B · all families shown, even after a FAIL</span></div>
    </div>
  );
}
export function BlockingRuleCallout({ rule, title, plain, observed, threshold, gap, candidate, onEvidence, source, ...rest }) {
  const bid = useId();
  return (
    <section {...rest} className="sn-block" aria-labelledby={bid}>
      <span className="sn-block-id" aria-label={`Blocking rule ${rule || 'not recorded'}`}>{rule || '—'}</span>
      <div className="sn-stack" style={{ gap: 4 }}>
        <span className="sn-micro">Blocking rule</span>
        <h3 className="sn-block-t" id={bid}>{title}</h3>
        <p className="sn-block-b">{plain}</p>
      </div>
      <div className="sn-block-nums">
        <StatCell size="sm" label="Observed" value={observed} />
        <StatCell size="sm" label="Threshold" value={threshold} />
        <StatCell size="sm" label="Gap" value={gap} />
        {candidate && <StatCell size="sm" label="Nearest candidate" value={candidate} />}
      </div>
      {(onEvidence || source) && <div className="sn-block-foot">{onEvidence && <button type="button" className="sn-link" onClick={onEvidence}>View evidence in the audit matrix<Icon name="ArrowRight" size={14} /></button>}{source && <span className="sn-small sn-faint">Rule text from {source}</span>}</div>}
    </section>
  );
}
export function RunDiffTable({ rows = [], from = 'previous run', to = 'this run', mutedDecision = true }) {
  return (
    <table className="sn-table sn-diff" aria-label={`Changes from ${from} to ${to}`}>
      <thead><tr><th>Field</th><th>{from}</th><th>{to}</th><th>Why it changed</th></tr></thead>
      <tbody>{rows.map(r => (
        <tr key={r.field}><td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{r.field}</td>
          <td className="was sn-num" style={{ fontSize: 12 }}>{r.was}</td>
          <td className="sn-num" style={{ fontSize: 12, color: 'var(--text-primary)' }}>{r.field === 'Decision' ? <DecisionBadge decision={r.now} size="compact" muted={mutedDecision} /> : r.field === 'Path' ? <PathChip path={r.now} /> : r.now}</td>
          <td style={{ whiteSpace: 'normal' }}>{r.why}</td></tr>
      ))}</tbody>
    </table>
  );
}
export function ReadinessChecklist({ items, title = 'Entry readiness' }) {
  const done = items.filter(i => i.state === 'done').length;
  const ic = { done: ['CircleCheck', 'var(--validation-pass)'], todo: ['Circle', 'var(--text-tertiary)'], blocked: ['OctagonX', 'var(--validation-fail)'], unconfirmed: ['CircleHelp', 'var(--validation-unconfirmed)'] };
  return (
    <div>
      <div className="sn-ready-progress"><span className="sn-num">{done} / {items.length}</span> complete · {title}</div>
      <ul className="sn-ready">
        {items.map((it, i) => (
          <li key={i}><Icon name={ic[it.state][0]} size={18} style={{ color: ic[it.state][1] }} label={it.state} />
            <span style={{ color: it.state === 'done' ? 'var(--text-secondary)' : 'var(--text-primary)' }}>{it.label}</span>
            <span className="sn-num sn-small sn-faint">{it.meta}</span>
            {it.detail && <span className="sn-ready-d">{it.detail}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
export function ProvenanceRow({ s, header }) {
  if (header) return <div className="sn-src" role="row" style={{ minHeight: 32, background: 'var(--table-header-bg)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500, borderBottom: '1px solid var(--border-strong)' }}><span role="columnheader">Source</span><span role="columnheader">State</span><span role="columnheader">Note</span><span role="columnheader" style={{ textAlign: 'right' }}>Last update (ET)</span><span role="columnheader" style={{ textAlign: 'right' }}>Age / budget</span></div>;
  return (
    <div className="sn-src" role="row" data-state={s.state.toLowerCase()}>
      <span className="sn-src-name" role="cell">{s.name}<small>{s.vendor}</small></span>
      <span role="cell"><DataStateBadge state={s.state} /></span>
      <span role="cell" className="sn-small" style={{ color: 'var(--text-secondary)' }}>{s.note || s.rows}</span>
      <span role="cell" className="sn-num sn-small" style={{ textAlign: 'right', color: 'var(--text-primary)' }}>{s.ts}</span>
      <span role="cell" className="sn-num sn-small" style={{ textAlign: 'right', color: s.state === 'LIVE' || s.state === 'FROZEN' ? 'var(--text-secondary)' : 'var(--provenance)' }}>{s.age} / {s.budget}</span>
    </div>
  );
}
const BANNER = {
  stub: ['FlaskRound', 'STUB DATA', 'Figures on this screen are synthetic placeholders, not market data. Decisions are suppressed to NO TRADE.'],
  unavailable: ['CircleSlash', 'SOURCE UNAVAILABLE', 'A source is not responding. Its values are withheld, not estimated.'],
  stale: ['Hourglass', 'STALE', 'A source is older than its freshness budget. Dependent rules are UNCONFIRMED.'],
  delayed: ['Clock', 'DELAYED', 'A source is vendor-delayed. Affected overlays are marked.'],
  'job-failed': ['TriangleAlert', 'JOB FAILED', 'A scheduled job did not complete. The last sealed decision stands.'],
  'kill-armed': ['Power', 'KILL SWITCH ARMED', 'Every checkpoint is forced to NO TRADE until an owner disarms it.'],
  holiday: ['CalendarOff', 'MARKET HOLIDAY', 'US cash markets are closed. No checkpoints will run.'],
  'early-close': ['Clock', 'EARLY CLOSE', 'The cash session closes early today; late checkpoints move with it.'],
};
export function DataBanner({ kind = 'stub', title, children, action, inline, ...rest }) {
  const [icon, t, b] = BANNER[kind];
  const alarm = kind === 'kill-armed' || kind === 'job-failed';
  return (
    <div {...rest} className="sn-banner" data-kind={kind} data-inline={inline || undefined} role={alarm ? 'alert' : 'status'}>
      <Icon name={icon} />
      <div className="sn-banner-t">{title || t}</div>
      {action && <div className="sn-banner-a">{action}</div>}
      <div className="sn-banner-b">{children || b}</div>
    </div>
  );
}

/* ---------- /pm ---------- */
export function KanbanCard({ c, ...rest }) {
  return (
    <button type="button" {...rest} className="sn-kcard" data-selected={c.sel || undefined} aria-roledescription="draggable card" aria-label={`${c.id}: ${c.t}. Priority ${c.prio} of 3, estimate ${c.est}`}>
      <span className="sn-row" style={{ gap: 6 }}><span className="sn-kcard-id">{c.id}</span>{(c.tags || []).map(t => <span key={t} className="sn-badge" style={{ height: 18, fontWeight: 500, letterSpacing: 0 }}>{t}</span>)}</span>
      <span className="sn-kcard-t">{c.t}</span>
      <span className="sn-kcard-f"><span className="sn-prio" data-level={c.prio} aria-label={`Priority ${c.prio} of 3`}><i /><i /><i /></span><span className="sn-num">{c.est}</span><span className="sn-sp" /><Avatar initials={c.who} name={c.who} size="sm" /></span>
    </button>
  );
}
export function DecisionLogEntry({ title, date, author, status = 'Accepted', children, alternatives = [], links }) {
  return (
    <article className="sn-dlog">
      <span className="sn-dlog-dot"><Icon name="Gavel" size={14} /></span>
      <div className="sn-stack" style={{ gap: 4 }}>
        <h3 className="sn-dlog-t">{title}</h3>
        <div className="sn-dlog-meta"><Badge>{status}</Badge><span className="sn-num">{date}</span><span>·</span><span>{author}</span>{links && <span>· {links}</span>}</div>
      </div>
      <p className="sn-dlog-b">{children}</p>
      {alternatives.length > 0 && <div className="sn-dlog-alts"><span className="sn-micro">Considered</span>{alternatives.map(a => <span key={a}>— {a}</span>)}</div>}
    </article>
  );
}

export function Panel({ title, meta, children, flush, icon, actions, id, style, className, bodyClassName, ...rest }) {
  const auto = useId(); id = id || auto;
  return (
    <section {...rest} className={cx('sn-panel', className)} aria-labelledby={title ? id : undefined} style={style}>
      {title && <div className="sn-panel-h">{icon && <Icon name={icon} style={{ color: 'var(--text-tertiary)' }} />}<h2 id={id}>{title}</h2><div className="sn-panel-meta">{meta}{actions}</div></div>}
      <div className={cx('sn-panel-b', flush && 'flush', bodyClassName)}>{children}</div>
    </section>
  );
}
